import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { UsuarioService } from 'src/app/services/usuario.service';

@Component({
    selector: 'app-condiciones',
    templateUrl: './condiciones.component.html',
    styleUrls: ['./condiciones.component.css'],
    standalone: false
})
export class CondicionesComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly _usuarioService = inject(UsuarioService);
  private readonly _orderComercioService = inject(OrderComercioService);
  private readonly _authService = inject(AuthService);
  private readonly _loadingService = inject(LoadingService);

  condiciones!: FormGroup;

  error = '';
  loading = false;

  img_receta?: File;
  preview_img_receta?: string;

  zona: string = '';
  mercadoPagoDisponible: boolean = false;

  // UI
  helpOpen = false;
  helpText = 'Podrás seleccionar la forma de entrega, la forma de pago y si tenés receta también podés subirla.';
  showEnvioMpWarning = false;

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    this.createForm();

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        const oc = this._orderComercioService.ordenComercio;
        if (oc.id_comercio) {
          const usuarioComercio = await firstValueFrom(
            this._usuarioService.getUsuarioXId(oc.id_comercio)
          );

          this.mercadoPagoDisponible = !!usuarioComercio?.x_mp_access_token?.trim();
        } else {
          this.mercadoPagoDisponible = false;
        }

        this.showForm();
      } catch {
        this.router.navigate(['/sign-in']);
      }
    });
  }

  private createForm(): void {
    this.condiciones = this.fb.group({
      b_retiro_local: [false],
      b_envio_domicilio: [false],
      b_pago_efectivo: [false],
      b_pago_mercadopago: [false],
    });
  }

  private showForm(): void {
    const oc = this._orderComercioService.ordenComercio;

    this.zona = oc.x_zona ?? '';

    this.condiciones.patchValue({
      b_retiro_local: !!oc.b_retiro_local,
      b_envio_domicilio: !!oc.b_envio_domicilio,
      b_pago_efectivo: !!oc.b_pago_efectivo,
      b_pago_mercadopago: this.mercadoPagoDisponible ? !!oc.b_pago_mercadopago : false,
    });

    if (!this.mercadoPagoDisponible) {
      this.condiciones.patchValue({
        b_pago_mercadopago: false,
      });
    }

    // restaurar receta
    this.img_receta = this._orderComercioService.img_receta;
    this.preview_img_receta = this._orderComercioService.preview_img_receta;
  }

  // reemplaza DOM directo: tooltip por estado
  openhelp(): void {
    this.helpOpen = !this.helpOpen;
  }

  onIntentarEnvioDomicilio(): void {
    if (!this.mercadoPagoDisponible) {
      this.showEnvioMpWarning = true;
      return;
    }

    this.showEnvioMpWarning = false;
  }

  saveCondiciones(): void {
    this.loading = true;
    this.error = '';

    const v = this.condiciones.getRawValue();

    if (!v.b_retiro_local && !v.b_envio_domicilio) {
      this.error = 'Debe Seleccionar una Forma de entrega';
      this.loading = false;
      return;
    }

    if (!v.b_pago_efectivo && !v.b_pago_mercadopago) {
      this.error = 'Debe Seleccionar una Forma de pago';
      this.loading = false;
      return;
    }

    if (!this.mercadoPagoDisponible && v.b_pago_mercadopago) {
      this.error = 'Este comercio no tiene Mercado Pago habilitado';
      this.loading = false;
      return;
    }

    if (!this.mercadoPagoDisponible && v.b_envio_domicilio) {
      this.error = 'Este comercio no permite envío a domicilio porque no tiene Mercado Pago habilitado';
      this.loading = false;
      return;
    }

    if (this.condiciones.invalid || this.error !== '') {
      this.loading = false;
      return;
    }

    const oc = this._orderComercioService.ordenComercio;

    oc.b_retiro_local = !!v.b_retiro_local;
    oc.b_envio_domicilio = !!v.b_envio_domicilio;
    oc.b_pago_efectivo = !!v.b_pago_efectivo;
    oc.b_pago_mercadopago = this.mercadoPagoDisponible ? !!v.b_pago_mercadopago : false;

    if (oc.b_envio_domicilio) {
      // Mantengo tu lógica original (zona sugerida)
      oc.x_envio_direccion = oc.x_zona ?? this.zona;
    } else {
      oc.x_envio_direccion = '';
    }

    this._orderComercioService.img_receta = this.img_receta;

    this.router.navigate(['/resumen-order-comercio']);
  }

  // manejamos los toggles sin depender del event.target
  control(controlName: 'b_retiro_local' | 'b_envio_domicilio' | 'b_pago_efectivo' | 'b_pago_mercadopago'): void {
    const form = this.condiciones;

    if (controlName !== 'b_envio_domicilio' && controlName !== 'b_pago_mercadopago' ) {
      this.showEnvioMpWarning = false;
    }

    const retiro = !!form.get('b_retiro_local')?.value;
    const envio = !!form.get('b_envio_domicilio')?.value;
    const efectivo = !!form.get('b_pago_efectivo')?.value;
    const mp = !!form.get('b_pago_mercadopago')?.value;

    if (controlName === 'b_retiro_local' && retiro) {
      form.patchValue({
        b_envio_domicilio: false,
      });
      return;
    }

    if (controlName === 'b_envio_domicilio' && envio) {
      if (!this.mercadoPagoDisponible) {
        form.patchValue({
          b_envio_domicilio: false,
          b_retiro_local: true,
          b_pago_efectivo: true,
          b_pago_mercadopago: false,
        });
        return;
      }
      
      form.patchValue({
        b_retiro_local: false,
        b_pago_efectivo: this.mercadoPagoDisponible ? false : true,
        b_pago_mercadopago: this.mercadoPagoDisponible ? true : false,
      });
      return;
    }

    if (controlName === 'b_pago_efectivo' && efectivo) {
      form.patchValue({
        b_pago_mercadopago: false,
        b_envio_domicilio: false,
        b_retiro_local: true,
      });
      return;
    }

    if (controlName === 'b_pago_mercadopago' && mp) {
      if (!this.mercadoPagoDisponible) {
        form.patchValue({
          b_pago_mercadopago: false,
        });
        return;
      }
      
      form.patchValue({
        b_pago_efectivo: false,
      });
      return;
    }
  }

   /**
   * Cámara / Galería:
   * - Nativo: Prompt (cámara o galería)
   * - Web/PWA: Photos (archivo/galería)
   */
  async capturarFileImgReceta(): Promise<void> {
    try {
      const source = Capacitor.isNativePlatform()
        ? CameraSource.Prompt
        : CameraSource.Photos;

      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source,
        quality: 90,
        allowEditing: false,
        promptLabelHeader: 'Seleccionar imagen',
        promptLabelPhoto: 'Galería',
        promptLabelPicture: 'Cámara',
      });

      if (!photo.dataUrl) return;

      this.preview_img_receta = photo.dataUrl;

      // base64 -> blob -> File
      const response = await fetch(photo.dataUrl);
      const blob = await response.blob();
      this.img_receta = new File([blob], 'receta.jpg', { type: blob.type || 'image/jpeg' });

      this._orderComercioService.img_receta = this.img_receta;
      this._orderComercioService.preview_img_receta = this.preview_img_receta;
    } catch (error) {
      // si el usuario cancela, Capacitor suele tirar error -> lo ignoramos
      console.error('Captura cancelada o error:', error);
    }
  }

  // Input file (por compatibilidad web): acá sí va tu "extraerBase64" pero bien armado
  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.error = 'El archivo seleccionado no es una imagen';
      input.value = '';
      return;
    }

    this.error = '';
    this.img_receta = file;
    this.preview_img_receta = await this.fileToDataUrl(file);
    // Guardo en el service para mantener la receta al volver atrás
    this._orderComercioService.img_receta = this.img_receta;
    this._orderComercioService.preview_img_receta = this.preview_img_receta;

    // para permitir volver a elegir el mismo archivo
    input.value = '';
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    });
  }

}
